/**
 * routes.js — All page components are lazy-loaded via React.lazy()
 * to enable route-level code splitting. This means each route's code
 * is only downloaded when a user actually navigates to that route,
 * drastically reducing the initial JavaScript bundle size.
 *
 * DO NOT convert these back to static imports — that will break code splitting.
 */

import React, { lazy } from "react";
import Icon from "@mui/material/Icon";
import { Contact, School } from "lucide-react";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import VideocamIcon from "@mui/icons-material/Videocam";
import { Grid3x3, ShoppingCart } from "lucide-react";
import {
  AttachFile,
  LibraryAdd,
  Money,
  CampaignOutlined,
  Schedule,
  Task,
  Settings,
  GradingOutlined,
  Equalizer,
  Business,
  FactCheckOutlined,
} from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CastForEducationIcon from "@mui/icons-material/CastForEducation";
import SchoolIcon from "@mui/icons-material/School";
import ClassIcon from "@mui/icons-material/Class";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import QuizIcon from "@mui/icons-material/Quiz";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ListAltIcon from "@mui/icons-material/ListAlt";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationImportantIcon from "@mui/icons-material/NotificationImportant";
import { BellRing } from "lucide-react";
import GradeIcon from "@mui/icons-material/Grade";

// Chunks are grouped by domain using webpackChunkName so Webpack bundles related
// pages together, cutting the number of network requests from 40+ down to 9.

// chunk: people — user & school management pages loaded early
const Dashboard = lazy(() => import(/* webpackChunkName: "people" */ "layouts/dashboard"));
const StaffTable = lazy(() =>
  import(/* webpackChunkName: "people" */ "layouts/tables/teachersTable"),
);
const Studentstables = lazy(() =>
  import(/* webpackChunkName: "people" */ "layouts/tables/studentsTable"),
);
const Classes = lazy(() => import(/* webpackChunkName: "people" */ "layouts/Classes"));
const Profile = lazy(() => import(/* webpackChunkName: "people" */ "layouts/profile"));
const Branches = lazy(() => import(/* webpackChunkName: "people" */ "layouts/Branches/Branches"));
const TeacherDashboard = lazy(() =>
  import(/* webpackChunkName: "people" */ "./layouts/teacherDashboard"),
);
const Departments = lazy(() => import(/* webpackChunkName: "people" */ "layouts/Departments"));

// chunk: registration — enrollment & roster pages
const Tables2 = lazy(() =>
  import(/* webpackChunkName: "registration" */ "layouts/tables/registerTable"),
);
const RegistrationTable = lazy(() =>
  import(/* webpackChunkName: "registration" */ "layouts/registerPage/RegistrationTable"),
);
const GenerateRoster = lazy(() =>
  import(/* webpackChunkName: "registration" */ "layouts/generateRoaster"),
);
const AssignPage = lazy(() => import(/* webpackChunkName: "registration" */ "layouts/assignPage"));
const AcademicStream = lazy(() =>
  import(/* webpackChunkName: "registration" */ "layouts/academicStream"),
);

// chunk: academics — exams, results, subjects
const Examtypes = lazy(() => import(/* webpackChunkName: "academics" */ "layouts/Examtype/index"));
const Subjects = lazy(() => import(/* webpackChunkName: "academics" */ "layouts/Subjects"));
const ExamResult = lazy(() => import(/* webpackChunkName: "academics" */ "layouts/examResult"));
const ResultTracking = lazy(() =>
  import(/* webpackChunkName: "academics" */ "layouts/ResultTracking"),
);
const GenerateExam = lazy(() => import(/* webpackChunkName: "academics" */ "layouts/ExamSheet"));
const ExamNotification = lazy(() =>
  import(/* webpackChunkName: "academics" */ "layouts/examNotification"),
);
const ExamGrading = lazy(() => import(/* webpackChunkName: "academics" */ "layouts/examGrading"));
const LiveExamination = lazy(() =>
  import(/* webpackChunkName: "academics" */ "layouts/LiveExamination"),
);
const VideoConference = lazy(() =>
  import(/* webpackChunkName: "academics" */ "layouts/VideoConference"),
);

// chunk: attendance — attendance & scheduling
const Attendance = lazy(() =>
  import(/* webpackChunkName: "attendance" */ "layouts/attendance/attendance"),
);
const SchoolPlan = lazy(() => import(/* webpackChunkName: "attendance" */ "layouts/schoolPlan"));
const ClassSchedule = lazy(() =>
  import(/* webpackChunkName: "attendance" */ "layouts/classSchedule.js"),
);
const LeaveFormat = lazy(() => import(/* webpackChunkName: "attendance" */ "layouts/leaveFormat"));

// chunk: comms — broadcast, notifications, feedback, communication
const Broadcast = lazy(() => import(/* webpackChunkName: "comms" */ "layouts/billing"));
const Notifications = lazy(() => import(/* webpackChunkName: "comms" */ "layouts/notifications"));
const CommunicationBook = lazy(() =>
  import(/* webpackChunkName: "comms" */ "layouts/communicationBook"),
);
const ParentFeedback = lazy(() =>
  import(/* webpackChunkName: "comms" */ "layouts/parentFeedback/Index"),
);
const SystemFeedback = lazy(() => import(/* webpackChunkName: "comms" */ "layouts/SystemFeedback"));
const PollManagement = lazy(() => import(/* webpackChunkName: "comms" */ "layouts/pollManagement"));

// chunk: finance — payments, procurement, school services
const Transactions = lazy(() => import(/* webpackChunkName: "finance" */ "layouts/payment"));
const Finance = lazy(() => import(/* webpackChunkName: "finance" */ "layouts/Finance"));
const ProcurementDashboard = lazy(() =>
  import(/* webpackChunkName: "finance" */ "layouts/procurement/index"),
);
const SchoolService = lazy(() => import(/* webpackChunkName: "finance" */ "layouts/SchoolService"));
const Transport = lazy(() => import(/* webpackChunkName: "finance" */ "layouts/Transport"));

// chunk: admin — analytics, audit, school data, admin tools
const SchoolData = lazy(() => import(/* webpackChunkName: "admin" */ "layouts/SchoolData"));
const Analytics = lazy(() => import(/* webpackChunkName: "admin" */ "layouts/analytics"));
const ActionAuditLog = lazy(() =>
  import(/* webpackChunkName: "admin" */ "layouts/ActionAuditLog/ActionAuditLog"),
);
const RequestPage = lazy(() => import(/* webpackChunkName: "admin" */ "layouts/request"));
const DownloadCertificate = lazy(() =>
  import(/* webpackChunkName: "admin" */ "layouts/DownloadCertificate/DownloadCertificate"),
);

// chunk: tools — tasks, file sharing, events
const Tasks = lazy(() => import(/* webpackChunkName: "tools" */ "./layouts/task"));
const FileShare = lazy(() => import(/* webpackChunkName: "tools" */ "layouts/fileShare"));
const EventsPage = lazy(() => import(/* webpackChunkName: "tools" */ "layouts/events"));

// chunk: library — library management
const LibraryComponent = lazy(() => import(/* webpackChunkName: "library" */ "layouts/library"));
const StaffLibraryDashboard = lazy(() =>
  import(/* webpackChunkName: "library" */ "layouts/staffLibrary"),
);

// chunk: auth — authentication pages
const Forgotten = lazy(() =>
  import(/* webpackChunkName: "auth" */ "layouts/authentication/forgottenpassword"),
);

import { Upload, User2, Users } from "lucide-react";
import assign from "./assets/images/assign.png";
// ─────────────────────────────────────────────────────────────────────────────

let routes = [];
routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
    features: [
      {
        name: "Student Overview",
        route: "/dashboard#students",
        icon: (
          <Icon fontSize="small" color="primary">
            person
          </Icon>
        ),
      },
      {
        name: "Staff Overview",
        route: "/dashboard#staff",
        icon: (
          <Icon fontSize="small" color="primary">
            group
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Branches",
    key: "branches",
    icon: <AccountTreeIcon />,
    route: "/branches",
    component: <Branches />,
  },
  {
    type: "collapse",
    name: "Home",
    key: "home",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/home",
    component: <TeacherDashboard />,
    features: [
      {
        name: "Quick Links",
        route: "/home#quick-links",
        icon: (
          <Icon fontSize="small" color="primary">
            link
          </Icon>
        ),
      },
      {
        name: "Recent Activity",
        route: "/home#recent-activity",
        icon: (
          <Icon fontSize="small" color="primary">
            history
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Attendance",
    key: "attendance",
    icon: <FactCheckOutlined />,
    route: "/attendance",
    component: <Attendance />,
    features: [
      {
        name: "Take Attendance",
        route: "/attendance/take",
        icon: (
          <Icon fontSize="small" color="primary">
            fact_check
          </Icon>
        ),
      },
      {
        name: "Whole Class Attendance",
        route: "/attendance/whole-class",
        icon: (
          <Icon fontSize="small" color="primary">
            groups
          </Icon>
        ),
      },
      {
        name: "Export Attendance",
        route: "/attendance/export",
        icon: (
          <Icon fontSize="small" color="primary">
            download
          </Icon>
        ),
      },
      {
        name: "Frequently Absent Students",
        route: "/attendance/frequent",
        icon: (
          <Icon fontSize="small" color="primary">
            event_busy
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Result",
    key: "result",
    icon: <PlaylistAddCheckIcon />,
    route: "/result",
    component: <ExamResult />,
    features: [
      {
        name: "Add Result",
        route: "/result/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Result List",
        route: "/result/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Message Broadcast",
    key: "broadcast",
    icon: <CampaignOutlined />,
    route: "/broadcast",
    component: <Broadcast />,
    features: [
      {
        name: "Broadcast to Students",
        route: "/broadcast/broadcast-to-students",
        icon: (
          <Icon fontSize="small" color="primary">
            school
          </Icon>
        ),
      },
      {
        name: "Broadcast to Staff",
        route: "/broadcast/broadcast-to-staff",
        icon: (
          <Icon fontSize="small" color="primary">
            badge
          </Icon>
        ),
      },
      {
        name: "Message Students",
        route: "/broadcast/message-students",
        icon: (
          <Icon fontSize="small" color="primary">
            chat
          </Icon>
        ),
      },
      {
        name: "Message Staff",
        route: "/broadcast/message-staff",
        icon: (
          <Icon fontSize="small" color="primary">
            forum
          </Icon>
        ),
      },
      {
        name: "Failed Messages",
        route: "/broadcast/failed-messages",
        icon: (
          <Icon fontSize="small" color="primary">
            sms_failed
          </Icon>
        ),
      },
      {
        name: "Message History",
        route: "/broadcast/message-history",
        icon: (
          <Icon fontSize="small" color="primary">
            history
          </Icon>
        ),
      },
    ],
  },

  {
    type: "collapse",
    name: "Communication Book",
    key: "communication-book",
    icon: <Icon fontSize="small">chat</Icon>,
    route: "/communication-book",
    component: <CommunicationBook />,
  },
  {
    type: "collapse",
    name: "Exam Notifications",
    key: "examNotifications",
    icon: <BellRing size={20} />, // Use BellRing for exam notifications
    route: "/examNotifications",
    component: <ExamNotification />,
    features: [
      {
        name: "Exam Alerts",
        route: "/examNotifications/alerts",
        icon: (
          <Icon fontSize="small" color="primary">
            notifications
          </Icon>
        ),
      },
      {
        name: "Exam Reminders",
        route: "/examNotifications/reminders",
        icon: (
          <Icon fontSize="small" color="primary">
            notifications_active
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Students",
    key: "students",
    icon: <SchoolIcon />,
    route: "/students",
    component: <Studentstables routename="Students" />,
    features: [
      {
        name: "Add Student",
        route: "/students/add",
        icon: (
          <Icon fontSize="small" color="primary">
            person_add
          </Icon>
        ),
      },
      {
        name: "Student List",
        route: "/students/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Staffs",
    key: "staffs",
    icon: <CastForEducationIcon />,
    route: "/staffs",
    component: <StaffTable routename="Teachers" />,
    features: [
      {
        name: "Add Staff",
        route: "/staffs/add",
        icon: (
          <Icon fontSize="small" color="primary">
            person_add
          </Icon>
        ),
      },
      {
        name: "Staff List",
        route: "/staffs/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
      {
        name: "Assign Class/Subject",
        route: "/staffs/assign",
        icon: (
          <Icon fontSize="small" color="primary">
            assignment_ind
          </Icon>
        ),
      },
      {
        name: "View Staff Activity",
        route: "/staffs/activity",
        icon: (
          <Icon fontSize="small" color="primary">
            history
          </Icon>
        ),
      },
      {
        name: "Bulk Image Upload",
        route: "/staffs/bulk-image-upload",
        icon: (
          <Icon fontSize="small" color="primary">
            photo_camera
          </Icon>
        ),
      },
      {
        name: "Withdrawn Staff List",
        route: "/staffs/withdrawn",
        icon: (
          <Icon fontSize="small" color="primary">
            person_off
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Departments",
    key: "departments",
    icon: <Business />,
    route: "/departments",
    component: <Departments />,
    features: [
      {
        name: "Add Department",
        route: "/departments/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Staff Digital Library",
    key: "staff-library",
    icon: <LibraryAdd />,
    route: "/library",
    component: <StaffLibraryDashboard />,
    features: [
      {
        name: "Dashboard",
        route: "/library",
        icon: (
          <Icon fontSize="small" color="primary">
            dashboard
          </Icon>
        ),
      },
      {
        name: "Upload Book",
        route: "/library/upload",
        icon: (
          <Icon fontSize="small" color="primary">
            upload
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Library Attendance",
    key: "library-attendance",
    icon: <PlaylistAddCheckIcon />,
    route: "/library-attendance",
    component: <LibraryComponent />,
  },
  {
    type: "collapse",
    name: "Tasks",
    key: "tasks",
    icon: <Task />,
    route: "/tasks",
    component: <Tasks />,
    features: [
      {
        name: "My Tasks",
        route: "/tasks/my-tasks",
        icon: (
          <Icon fontSize="small" color="primary">
            check_circle
          </Icon>
        ),
      },
      {
        name: "Task History",
        route: "/tasks/history",
        icon: (
          <Icon fontSize="small" color="primary">
            history
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Classes",
    key: "classes",
    icon: <ClassIcon />,
    route: "/classes",
    component: <Classes />,
    features: [
      {
        name: "Add Class",
        route: "/classes/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Class List",
        route: "/classes/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Exam",
    key: "examtypes",
    icon: <QuizIcon />,
    route: "/examtypes",
    component: <Examtypes />,
    features: [
      {
        name: "Add Exam",
        route: "/examtypes/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Assign Examiners",
        route: "/examtypes/assign",
        icon: (
          <Icon fontSize="small" color="primary">
            assignment_ind
          </Icon>
        ),
      },
      {
        name: "Bulk Assign Class & Subject",
        route: "/examtypes/bulk-assign",
        icon: (
          <Icon fontSize="small" color="primary">
            playlist_add_check
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "File Share",
    key: "fileshare",
    icon: <AttachFile />,
    route: "/fileshare",
    component: <FileShare />,
    features: [
      {
        name: "Upload File",
        route: "/fileshare/upload",
        icon: (
          <Icon fontSize="small" color="primary">
            upload
          </Icon>
        ),
      },
      {
        name: "Shared Files",
        route: "/fileshare/shared",
        icon: (
          <Icon fontSize="small" color="primary">
            folder_shared
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Live Examination",
    key: "live-examination",
    icon: <ScreenShareIcon />,
    route: "/live-examination",
    component: <LiveExamination />,
  },
  {
    type: "collapse",
    name: "Video Conference",
    key: "video-conference",
    icon: <VideocamIcon />,
    route: "/video-conference",
    component: <VideoConference />,
  },
  {
    type: "collapse",
    name: "Subjects",
    key: "subjects",
    icon: <AutoStoriesIcon />,
    route: "/subjects",
    component: <Subjects />,
    features: [
      {
        name: "Add Subject",
        route: "/subjects/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Subject List",
        route: "/subjects/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Transactions",
    key: "transactions",
    icon: <Money />,
    route: "/transactions",
    component: <Transactions />,
    features: [
      {
        name: "New Transaction",
        route: "/transactions/new",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Transaction History",
        route: "/transactions/history",
        icon: (
          <Icon fontSize="small" color="primary">
            history
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Schedule",
    key: "schedules",
    icon: <Schedule />,
    route: "/schedules",
    component: <ClassSchedule />,
    features: [
      {
        name: "Add Schedule",
        route: "/schedules/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Schedule List",
        route: "/schedules/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "School Plans",
    key: "schoolPlans",
    icon: <Icon fontSize="small">alarm</Icon>,
    route: "/schoolPlans",
    component: <SchoolPlan />,
    features: [
      {
        name: "Create Academic Year",
        route: "/schoolPlans/create-academic-year",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Create Semester",
        route: "/schoolPlans/create-semester",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Parent Feedback",
    key: "parentfeedback",
    icon: <Users />,
    route: "/parentfeedback",
    component: <ParentFeedback />,
    features: [
      {
        name: "View Feedback",
        route: "/parentfeedback/list",
        icon: (
          <Icon fontSize="small" color="primary">
            feedback
          </Icon>
        ),
      },
      {
        name: "Reply Feedback",
        route: "/parentfeedback/reply",
        icon: (
          <Icon fontSize="small" color="primary">
            reply
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "System Feedback",
    key: "systemfeedback",
    icon: <Icon fontSize="small">reviews</Icon>,
    route: "/systemfeedback",
    component: <SystemFeedback />,
    features: [
      {
        name: "All Feedback",
        route: "/systemfeedback",
        icon: (
          <Icon fontSize="small" color="primary">
            feedback
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Academic Stream",
    key: "academicstream",
    icon: <AutoStoriesIcon />,
    route: "/academicstream",
    component: <AcademicStream />,
    features: [
      {
        name: "Add Stream",
        route: "/academicstream/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Stream List",
        route: "/academicstream/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "School Data",
    key: "schooldata",
    icon: <School />,
    route: "/schooldata",
    component: <SchoolData />,
    features: [
      {
        name: "School-based Restrictions",
        route: "/schooldata/restrictions",
        icon: (
          <Icon fontSize="small" color="primary">
            rule
          </Icon>
        ),
      },
      {
        name: "Update Data",
        route: "/schooldata/update",
        icon: (
          <Icon fontSize="small" color="primary">
            edit
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Register",
    key: "register",
    icon: <Grid3x3 />,
    route: "/register",
    component: <RegistrationTable />,
  },
  {
    type: "collapse",
    name: "Procurement",
    key: "procurement",
    icon: <Inventory2OutlinedIcon />,
    route: "/procurement",
    component: <ProcurementDashboard />,
  },
  {
    type: "collapse",
    name: "Events",
    key: "events",
    icon: <Icon fontSize="small">event</Icon>,
    route: "/events",
    component: <EventsPage />,
    features: [
      {
        name: "Add Event",
        route: "/events/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Event List",
        route: "/events/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Question",
    key: "question",
    icon: <QuizIcon />,
    route: "/question",
    component: <GenerateExam />,
    features: [
      {
        name: "Add Question",
        route: "/question/add",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Question List",
        route: "/question/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Notifications",
    key: "notifications",
    icon: <NotificationsIcon />, // Keep as is for general notifications
    route: "/notifications",
    component: <Notifications />,
    features: [
      {
        name: "All Notifications",
        route: "/notifications/all",
        icon: (
          <Icon fontSize="small" color="primary">
            notifications
          </Icon>
        ),
      },
      {
        name: "Unread Notifications",
        route: "/notifications/unread",
        icon: (
          <Icon fontSize="small" color="primary">
            notifications_active
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Requests",
    key: "requests",
    icon: <QuestionMarkIcon />,
    route: "/requests",
    component: <RequestPage />,
    features: [
      {
        name: "New Request",
        route: "/requests/new",
        icon: (
          <Icon fontSize="small" color="primary">
            add
          </Icon>
        ),
      },
      {
        name: "Request List",
        route: "/requests/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Exam Grading",
    key: "exam-grading",
    icon: <GradeIcon />, // Use GradeIcon for Exam Grading
    route: "/exam-grading",
    component: <ExamGrading />,
    features: [
      {
        name: "Grade Exam",
        route: "/exam-grading/grade",
        icon: (
          <Icon fontSize="small" color="primary">
            grade
          </Icon>
        ),
      },
      {
        name: "Grading List",
        route: "/exam-grading/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Assign",
    key: "assign",
    icon: (
      <Icon fontSize="small">
        <img src={assign} alt="assign class" />
      </Icon>
    ),
    route: "/assign",
    component: <AssignPage />,
    features: [
      {
        name: "Assign Class",
        route: "/assign/class",
        icon: (
          <Icon fontSize="small" color="primary">
            class_
          </Icon>
        ),
      },
      {
        name: "Assign Subject",
        route: "/assign/subject",
        icon: (
          <Icon fontSize="small" color="primary">
            subject
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Roster",
    key: "generate",
    icon: <Settings />,
    route: "/generate",
    component: <GenerateRoster />,
    features: [
      {
        name: "Generate Roster",
        route: "/generate/roster",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
      {
        name: "Roster Settings",
        route: "/generate/settings",
        icon: (
          <Icon fontSize="small" color="primary">
            settings
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Result Tracking",
    key: "result-tracking",
    icon: <ListAltIcon />,
    route: "/result-tracking",
    component: <ResultTracking />,
  },
  {
    type: "collapse",
    name: "Analytics",
    key: "analytics",
    icon: <Equalizer />,
    route: "/analytics",
    component: <Analytics />,
    features: [
      {
        name: "View Analytics",
        route: "/analytics/view",
        icon: (
          <Icon fontSize="small" color="primary">
            bar_chart
          </Icon>
        ),
      },
      {
        name: "Export Analytics",
        route: "/analytics/export",
        icon: (
          <Icon fontSize="small" color="primary">
            download
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Service",
    key: "service",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/service",
    component: <SchoolService />,
  },
  {
    type: "collapse",
    name: "Finance",
    key: "finance",
    icon: <Icon fontSize="small">payment</Icon>,
    route: "/finance",
    component: <Finance />,
  },
  {
    type: "collapse",
    name: "Transport",
    key: "transport",
    icon: <Icon fontSize="small">directions_bus</Icon>,
    route: "/transport",
    component: <Transport />,
    features: [
      {
        name: "Live Fleet Overview",
        route: "/transport/live-fleet-overview",
        icon: (
          <Icon fontSize="small" color="primary">
            sensors
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Poll Management",
    key: "poll-management",
    icon: <Icon fontSize="small">poll</Icon>,
    route: "/poll-management",
    component: <PollManagement />,
    features: [
      {
        name: "Create Poll",
        route: "/poll-management/create",
        icon: (
          <Icon fontSize="small" color="primary">
            add_circle
          </Icon>
        ),
      },
      {
        name: "Manage Polls",
        route: "/poll-management/list",
        icon: (
          <Icon fontSize="small" color="primary">
            ballot
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Audit Logs",
    key: "audit-logs",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/audit-logs",
    component: <ActionAuditLog />,
  },
  {
    type: "collapse",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
    features: [
      {
        name: "Edit Profile",
        route: "/profile/edit",
        icon: (
          <Icon fontSize="small" color="primary">
            edit
          </Icon>
        ),
      },
      {
        name: "Profile Settings",
        route: "/profile/settings",
        icon: (
          <Icon fontSize="small" color="primary">
            settings
          </Icon>
        ),
      },
    ],
  },
  {
    type: "collapse",
    name: "Reset-password",
    key: "reset-password",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/reset",
    component: <Forgotten />,
    features: [
      {
        name: "Request Reset",
        route: "/reset/request",
        icon: (
          <Icon fontSize="small" color="primary">
            lock_reset
          </Icon>
        ),
      },
      {
        name: "Reset List",
        route: "/reset/list",
        icon: (
          <Icon fontSize="small" color="primary">
            list
          </Icon>
        ),
      },
    ],
  },
];

export { routes };
export default routes;
